// bluaIQ — ChangeOrder state machine. PURE, no I/O.
//
// Closes the ChangeOrder half of the "three remaining Day-1 money write paths"
// (D-25AGO-09), against docs/SPEC_STATE_TRANSITIONS_MONEY_CLUSTER_V1.md §2.
//
// THE GAP THIS CLOSES. Before this module there was NO PATCH/PUT/DELETE for a
// change order anywhere in the server — the only write was `changeOrder.create`.
// Every change order was therefore permanently `PENDING`, `approvedAt` was
// dead-on-write, and three dashboard/AIA queries that filter `status:'APPROVED'`
// summed a set that was STRUCTURALLY always empty and displayed the zero as
// financial fact (spec F-3). Approve/reject is the missing verb.
//
// VOCABULARY: THE PRODUCT'S, NOT THE SPEC'S DRAFT-FIRST SKETCH. The spec §2.2
// sketches DRAFT → PENDING_APPROVAL → APPROVED|REJECTED with a REJECTED → DRAFT
// "reopen" edge. The SHIPPED product has no DRAFT: `ChangeOrder.status` defaults
// to `"PENDING"` (schema.prisma), `ChangeOrderSchema` carries no `status` key so
// a client-sent `DRAFT` is stripped by zod, and the schema's own comment reads
// `PENDING | APPROVED | REJECTED`. The persisted `PENDING` IS the spec's
// "awaiting approval" state. This module implements the spec's edges over the
// vocabulary the database actually holds — it INVENTS no state.
//
//   PENDING ──approve──▶ APPROVED  (terminal)
//      │
//      └────reject────▶ REJECTED  (terminal in v1)
//
// WHY REJECTED IS TERMINAL HERE. The spec's only permitted outbound edge from
// REJECTED is `reopen → DRAFT`, and it lists `REJECTED → PENDING_APPROVAL` as
// explicitly PROHIBITED. DRAFT does not exist in this product, so the reopen
// edge has no legal destination and REJECTED has no outbound edge in v1.
// Re-routing reopen to PENDING would be exactly the prohibited edge. A rejected
// change order is superseded by a NEW change order, never by an un-reject.
//
// WHY APPROVED IS IRREVERSIBLE. It feeds contract-sum arithmetic and the
// dashboard totals; reversing it silently restates money already shown to a
// client. Un-approval, if ever needed, is a new compensating change order.
//
// FIELD EDITS ARE NOT MODELLED, DELIBERATELY. Spec §2.2 MUST: amount,
// description, reason, phase and impactDays are immutable once the order leaves
// DRAFT. Every persisted row is already past DRAFT, so there is no state in
// which a financial-term edit is permitted — and therefore no edit verb here.

export const CHANGE_ORDER_STATUSES = ['PENDING', 'APPROVED', 'REJECTED'] as const
export type ChangeOrderStatus = (typeof CHANGE_ORDER_STATUSES)[number]

/** Permitted edges. Everything not listed is PROHIBITED (spec §2.2). */
const PERMITTED: Record<ChangeOrderStatus, readonly ChangeOrderStatus[]> = {
  PENDING: ['APPROVED', 'REJECTED'],
  APPROVED: [], // terminal — irreversible, feeds contract sum
  REJECTED: [], // terminal in v1 — reopen needs a DRAFT state the product lacks
}

export function isChangeOrderStatus(value: unknown): value is ChangeOrderStatus {
  return typeof value === 'string' && (CHANGE_ORDER_STATUSES as readonly string[]).includes(value)
}

/**
 * True iff `from → to` is a permitted edge. A self-transition (`from === to`)
 * is NEVER a valid transition — it is not an edge, and treating it as one would
 * mask a double-submit (APPROVED→APPROVED). Callers must handle "status
 * unchanged" separately, as a no-op, before asking this.
 */
export function isValidChangeOrderTransition(from: ChangeOrderStatus, to: ChangeOrderStatus): boolean {
  if (from === to) return false
  return PERMITTED[from]?.includes(to) ?? false
}

/** True iff the status has no outbound edge at all (spec R-1.2.d: terminal
 *  states are NAMED as terminal, not left implicit). */
export function isTerminalChangeOrderStatus(status: ChangeOrderStatus): boolean {
  return PERMITTED[status].length === 0
}

/** Audit event name per edge, so the operational log says WHICH edge ran rather
 *  than one fused "updated" (spec §2.3 heading 4). Assumes the edge is valid. */
export function changeOrderTransitionEventName(from: ChangeOrderStatus, to: ChangeOrderStatus): string {
  if (from === 'PENDING' && to === 'APPROVED') return 'change_order_approved'
  if (from === 'PENDING' && to === 'REJECTED') return 'change_order_rejected'
  return 'change_order_transitioned'
}

/** A rejection MUST carry a reason (spec §2.3 heading 3). Trimmed emptiness is
 *  not a reason. Kept here, next to the edge it guards, so the route and the
 *  tests read the SAME rule. */
export function rejectionReasonIsPresent(reason: unknown): reason is string {
  return typeof reason === 'string' && reason.trim().length > 0
}
